/**
 * @fileoverview StepIndicator Component - Workflow Progress Display
 *
 * This component displays the current progress through the application's
 * multi-step workflow. Shows completed, current, and upcoming steps.
 *
 * @description
 * Key features:
 * - Visual step indicators with numbers/checkmarks
 * - Highlights current step
 * - Shows completion status
 * - Responsive design (hides labels on small screens)
 * - Supports different step arrays for images vs videos
 *
 * @requires React
 * @requires ../utils/constants - Step labels array
 */
import React from 'react';
import { STEPS } from '../utils/constants';

/**
 * StepIndicator Component
 *
 * @param {Object} props
 * @param {number} props.currentStep - The current step index (0-based)
 * @param {boolean} props.allDownloaded - Whether all files have been downloaded
 * @param {Array} props.steps - Array of step names (defaults to STEPS)
 * @returns {JSX.Element}
 */
function StepIndicator({ currentStep, allDownloaded, steps = STEPS }) {
  const lastStepIndex = steps.length - 1;
  /* ========================================================================
     Render
     ======================================================================== */

  return (
    <div className="flex items-center justify-center gap-2 mb-4 flex-wrap">
      {steps.map((stepName, i) => {
        const isComplete = currentStep > i || (i === lastStepIndex && allDownloaded);
        const isCurrent = currentStep === i && !(i === lastStepIndex && allDownloaded);

        return (
          <div key={i} className="flex items-center">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
              isCurrent
                ? 'bg-emerald-500/20 border border-emerald-500 text-white'
                : isComplete
                  ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                  : 'bg-white/5 border border-white/10 text-gray-500'
            }`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                isCurrent ? 'bg-emerald-500 text-white' : isComplete ? 'bg-green-500 text-white' : 'bg-white/10'
              }`}>
                {isComplete ? '✓' : i + 1}
              </span>
              <span className="font-medium hidden sm:inline">{stepName}</span>
            </div>
            {i < lastStepIndex && (
              <div className={`w-8 h-0.5 mx-1 ${currentStep > i ? 'bg-green-500/50' : 'bg-white/10'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default StepIndicator;
